/*
 * Mesh sidecar + plane client.
 *
 * Binds to VITE_MESH_BASE (default http://127.0.0.1:9085) for the Transport
 * sidecar's own surface:
 *   - getHealth()         — /healthz, the green-dot gate
 *   - getHealthProbe()    — /v1/health/{live,ready,startup}
 *   - getDispatch(id)     — /v1/dispatch/{request_id}
 *   - getMetricsText()    — raw /v1/metrics Prometheus TEXT (parsed elsewhere)
 *   - getOpenApi()        — /openapi.json self-description
 *
 * The four non-Transport planes (trust/catalog/audit/consent) are reached
 * SAME-ORIGIN through the console nginx at /v1/planes/<plane>/<rest>, which
 * the proxy rewrites to mesh-<plane>:9080/<rest>. `trust.* catalog.* audit.*
 * consent.*` below bind to the real read routes those services now serve.
 *
 * Honesty rule: we NEVER invent endpoints. A capability whose backend route
 * does not exist yet (Trust federation peers, Catalog Pact state, a single
 * audit entry by hash, the forgetting service, RTBF, SD-JWT-VC verification)
 * stays a `PlaneOfflineError` stub — wiring it later means deleting the
 * throw, exactly as before.
 *
 * Two distinct failure shapes reach the caller:
 *   - `PlaneOfflineError` — the backend is genuinely unreachable (network
 *     failure, or a 502 from the proxy meaning the upstream container is
 *     down). Views render the dormant/offline panel.
 *   - `MeshApiError`      — the backend answered but with a non-OK status
 *     (403 access-gated, 404 empty-state, etc). Views branch on `.status`
 *     to render the HONEST specific state — never lumped in with "offline".
 */

import type { DispatchReceipt, HealthState, HealthProbe } from "./types";

/** Configured sidecar base URL. */
export const MESH_BASE: string =
  (import.meta.env?.VITE_MESH_BASE as string | undefined)?.replace(/\/$/, "") ??
  "http://127.0.0.1:9085";

/** Thrown when a plane's backing service is genuinely unreachable. */
export class PlaneOfflineError extends Error {
  readonly plane: string;
  readonly service: string;
  readonly hint: string;
  constructor(plane: string, service: string, hint?: string) {
    super(`${plane} plane offline — awaiting ${service}`);
    this.name = "PlaneOfflineError";
    this.plane = plane;
    this.service = service;
    this.hint = hint ?? `The ${service} service is not running in this deployment.`;
  }
}

export function isPlaneOffline(e: unknown): e is PlaneOfflineError {
  return e instanceof PlaneOfflineError;
}

/** Thrown when a plane answers with a non-OK HTTP status, or is unreachable
 * on the sidecar's own surface (getHealth/getDispatch/getMetricsText). */
export class MeshApiError extends Error {
  readonly status: number;
  readonly url: string;
  constructor(message: string, status: number, url: string) {
    super(message);
    this.name = "MeshApiError";
    this.status = status;
    this.url = url;
  }
}

export function isMeshApiError(e: unknown): e is MeshApiError {
  return e instanceof MeshApiError;
}

interface RequestOpts {
  signal?: AbortSignal;
  timeoutMs?: number;
}

function url(path: string): string {
  return `${MESH_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

async function rawFetch(path: string, opts: RequestOpts = {}): Promise<Response> {
  const { signal, timeoutMs = 6000 } = opts;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const onAbort = () => ctrl.abort();
  signal?.addEventListener("abort", onAbort, { once: true });
  try {
    return await fetch(url(path), {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: ctrl.signal,
      // No credentials, no third-party — same-origin posture.
      mode: "cors",
      credentials: "omit",
    });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}

async function getJson<T>(path: string, opts?: RequestOpts): Promise<T> {
  let res: Response;
  try {
    res = await rawFetch(path, opts);
  } catch (e) {
    throw new MeshApiError(
      e instanceof Error ? e.message : "network error",
      0,
      url(path),
    );
  }
  if (!res.ok) {
    throw new MeshApiError(`HTTP ${res.status} for ${path}`, res.status, url(path));
  }
  return (await res.json()) as T;
}

/* ---------- Health ---------- */

/** The connection gate. Green dot only when this returns healthy: true. */
export async function getHealth(opts?: RequestOpts): Promise<HealthState> {
  const started = performance.now();
  try {
    const res = await rawFetch("/healthz", { timeoutMs: 4000, ...opts });
    const latencyMs = Math.round(performance.now() - started);
    return {
      healthy: res.ok,
      status: res.status,
      checkedAt: Date.now(),
      latencyMs,
      ...(res.ok ? {} : { error: `sidecar returned HTTP ${res.status}` }),
    };
  } catch (e) {
    return {
      healthy: false,
      status: 0,
      checkedAt: Date.now(),
      error: e instanceof Error ? e.message : "unreachable",
    };
  }
}

/** Individual liveness/readiness/startup probe under /v1/health/{probe}. */
export async function getHealthProbe(
  probe: HealthProbe,
  opts?: RequestOpts,
): Promise<boolean> {
  try {
    const res = await rawFetch(`/v1/health/${probe}`, { timeoutMs: 4000, ...opts });
    return res.ok;
  } catch {
    return false;
  }
}

/* ---------- Dispatch ---------- */

/** Look up a single dispatch by request_id. The Transport drill-in. */
export function getDispatch(requestId: string, opts?: RequestOpts): Promise<DispatchReceipt> {
  return getJson<DispatchReceipt>(`/v1/dispatch/${encodeURIComponent(requestId)}`, opts);
}

/* ---------- Metrics (raw text — parsed in metrics.ts) ---------- */

/** Fetch the raw Prometheus TEXT exposition from /v1/metrics. */
export async function getMetricsText(opts?: RequestOpts): Promise<string> {
  let res: Response;
  try {
    res = await rawFetch("/v1/metrics", { timeoutMs: 5000, ...opts });
  } catch (e) {
    throw new MeshApiError(
      e instanceof Error ? e.message : "network error",
      0,
      url("/v1/metrics"),
    );
  }
  if (!res.ok) {
    throw new MeshApiError(`HTTP ${res.status} for /v1/metrics`, res.status, url("/v1/metrics"));
  }
  return res.text();
}

/* ---------- OpenAPI self-description ---------- */

export function getOpenApi(opts?: RequestOpts): Promise<unknown> {
  return getJson<unknown>("/openapi.json", opts);
}

/* ---------- Namespaced plane surfaces ----------
 *
 * Each namespace binds to the SAME-ORIGIN proxy at /v1/planes/<plane>/<rest>.
 * A capability whose backend route genuinely does not exist yet stays a
 * PlaneOfflineError stub (see the module doc comment) — never a guessed shape.
 */

// Returns (does NOT throw) the error, so `Promise.reject(offline(...))` rejects
// lazily. Throwing here would escape the promise — it runs before Promise.reject.
function offline(plane: string, service: string, hint?: string): PlaneOfflineError {
  return new PlaneOfflineError(plane, service, hint);
}

/* ---------- Per-plane health probes ----------
 *
 * The four non-Transport planes are deployed containers, each reachable
 * SAME-ORIGIN via the console nginx at /v1/planes/<plane>/healthz (proxied to
 * mesh-<plane>:9080/healthz). A successful probe means "service deployed,
 * health 200"; the business routes below are the real read-APIs those
 * services now serve, wired one by one as they land.
 */

/** The four non-Transport planes that expose a same-origin health stub. */
export type PlaneName = "trust" | "catalog" | "audit" | "consent";

/** Outcome of a plane health probe: up/down plus the round-trip latency. */
export interface PlaneHealth {
  up: boolean;
  latencyMs: number;
}

/**
 * Probe one plane's health stub through the same-origin nginx proxy. Resolves
 * to {up:false} on any non-OK status, timeout, or network error — it never
 * throws, so callers branch on `up` alone. Uses the same abort/timeout posture
 * as rawFetch but against the SAME-ORIGIN proxy path (not MESH_BASE), since
 * plane health is reached through the console's own nginx, not the sidecar.
 */
async function probePlaneHealth(
  plane: PlaneName,
  opts: RequestOpts = {},
): Promise<PlaneHealth> {
  const { signal, timeoutMs = 4000 } = opts;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const onAbort = () => ctrl.abort();
  signal?.addEventListener("abort", onAbort, { once: true });
  const started = performance.now();
  try {
    const res = await fetch(`/v1/planes/${plane}/healthz`, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: ctrl.signal,
      mode: "same-origin",
      credentials: "omit",
    });
    return { up: res.ok, latencyMs: Math.round(performance.now() - started) };
  } catch {
    return { up: false, latencyMs: Math.round(performance.now() - started) };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}

/**
 * GET a business route from a plane through the same-origin proxy.
 *
 * A network failure or a 502 (the proxy's "upstream unreachable" status)
 * means the backing SERVICE is down — that is the honest `PlaneOfflineError`
 * case. Any other non-OK status (403 access-gated, 404 empty-state, ...)
 * means the service answered — that is a real `MeshApiError`, carrying the
 * status so views can render the SPECIFIC honest state rather than folding
 * it into "offline".
 */
async function planeFetchJson<T>(
  plane: PlaneName,
  path: string,
  service: string,
  opts: RequestOpts = {},
): Promise<T> {
  const { signal, timeoutMs = 6000 } = opts;
  const fullPath = `/v1/planes/${plane}${path.startsWith("/") ? path : `/${path}`}`;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const onAbort = () => ctrl.abort();
  signal?.addEventListener("abort", onAbort, { once: true });
  let res: Response;
  try {
    res = await fetch(fullPath, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: ctrl.signal,
      mode: "same-origin",
      credentials: "omit",
    });
  } catch {
    throw offline(planeLabel(plane), service);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
  if (res.status === 502) {
    // The proxy could reach nginx but not the upstream container.
    throw offline(planeLabel(plane), service, `${service} is unreachable (502 from the proxy).`);
  }
  if (!res.ok) {
    let detail = "";
    try {
      const body = (await res.json()) as { detail?: unknown };
      if (typeof body?.detail === "string") detail = body.detail;
    } catch {
      // body wasn't JSON — fall through to the generic message.
    }
    throw new MeshApiError(
      detail || `HTTP ${res.status} for ${fullPath}`,
      res.status,
      fullPath,
    );
  }
  return (await res.json()) as T;
}

/** Display label for a PlaneName, matching the existing offline()-call usage. */
function planeLabel(plane: PlaneName): string {
  switch (plane) {
    case "trust":
      return "Trust";
    case "catalog":
      return "Catalog";
    case "audit":
      return "Observability";
    case "consent":
      return "Consent";
  }
}

/* ============================================================ *
 * Trust plane — PKI, registry, RFC 6962 Merkle log (Part III).  *
 * Wire shapes below mirror services/trust_plane/app.py exactly. *
 * ============================================================ */

/** One row of the §21.1 signed participant registry. */
export interface RegistryParticipant {
  id: string;
  legal_name: string;
  country: string;
  status: string;
  active_keys: unknown[];
}

/** GET /v1/registry/participants — §21.1 SignedRegistry snapshot. */
export interface RegistrySnapshot {
  version: number;
  country: string;
  snapshot_at: string;
  participants: RegistryParticipant[];
  signature: { signature_jws: string };
}

/** GET /v1/log/sth — the latest published anchor, RFC 6962 STH-shaped. */
export interface SignedTreeHead {
  version: string;
  country: string;
  anchor_date: string;
  tree_size: number;
  tree_head_hash: string;
  signed_at: string;
  issuing_ca_kid: string;
  spec_version: string;
  signature_jws: string;
}

/** GET /v1/log/proof?leaf=… — RFC 6962 §2.1.3 inclusion proof by leaf hash. */
export interface InclusionProofResult {
  seq: number;
  anchor_date: string;
  tree_size: number;
  audit_path_hex: string[];
}

/** Trust plane — PKI, registry, RFC 6962 Merkle log, federation cross-signing. */
export const trust = {
  /** GET /v1/planes/trust/healthz — deployed-stub health probe. */
  health(opts?: RequestOpts): Promise<PlaneHealth> {
    return probePlaneHealth("trust", opts);
  },
  /** GET /v1/registry/participants — directory of mesh participants. */
  listParticipants(): Promise<RegistrySnapshot> {
    return planeFetchJson("trust", "/v1/registry/participants", "trust-registry");
  },
  /** GET /v1/log/sth — signed tree head of the RFC 6962 transparency log.
   * 404 when no anchor has been published yet (empty state, not an error). */
  getSignedTreeHead(): Promise<SignedTreeHead> {
    return planeFetchJson("trust", "/v1/log/sth", "trust-log");
  },
  /** GET /v1/log/proof?leaf=… — Merkle inclusion proof for a leaf. */
  getInclusionProof(leaf: string): Promise<InclusionProofResult> {
    return planeFetchJson(
      "trust",
      `/v1/log/proof?leaf=${encodeURIComponent(leaf)}`,
      "trust-log",
    );
  },
  /** GET /v1/federation/peers — cross-signed federation peers.
   * No such route is served yet — stays a genuine offline stub. */
  listFederationPeers(): Promise<never> {
    return Promise.reject(offline("Trust", "trust-federation"));
  },
};

/* ============================================================ *
 * Catalog plane — publication, oasdiff (Part V).                *
 * Wire shapes mirror services/catalog_plane/read_api.py.        *
 * ============================================================ */

/** One row of the §35.1 published-service discovery list. */
export interface CatalogServiceRow {
  service_id: string;
  country: string;
  publisher_member_id: string;
  title: string;
  latest_version: string;
  state: string;
  bundle_sha256_hex: string;
  updated_at: string;
}

/** GET /v1/catalog/services */
export interface CatalogServicesResponse {
  items: CatalogServiceRow[];
  generated_at: string;
}

/** One oasdiff-classified change. */
export interface CatalogDiffChange {
  path: string;
  kind: string;
  breaking: boolean;
}

/** GET /v1/catalog/services/{id}/diff */
export interface CatalogDiffResult {
  service_id: string;
  from_version: string | null;
  to_version: string | null;
  is_breaking: boolean;
  verdict: string;
  changes: CatalogDiffChange[];
}

/** Catalog plane — OpenAPI/AsyncAPI publication, oasdiff, Pact, public mirror. */
export const catalog = {
  /** GET /v1/planes/catalog/healthz — deployed-stub health probe. */
  health(opts?: RequestOpts): Promise<PlaneHealth> {
    return probePlaneHealth("catalog", opts);
  },
  /** GET /v1/catalog/services — published service catalogue. */
  listServices(): Promise<CatalogServicesResponse> {
    return planeFetchJson("catalog", "/v1/catalog/services", "catalog-service");
  },
  /** GET /v1/catalog/services/{id}/diff — oasdiff against prior version.
   * 404 for an unknown service id. */
  getServiceDiff(id: string): Promise<CatalogDiffResult> {
    return planeFetchJson(
      "catalog",
      `/v1/catalog/services/${encodeURIComponent(id)}/diff`,
      "catalog-service",
    );
  },
  /** GET /v1/catalog/pact/{id} — Pact contract verification state.
   * No such route is served yet — stays a genuine offline stub. */
  getPactState(_id: string): Promise<never> {
    return Promise.reject(offline("Catalog", "catalog-pact"));
  },
};

/* ============================================================ *
 * Consent plane — consent records (Part VII).                   *
 * Wire shapes mirror services/consent_plane/read_api.py.        *
 * The list/get routes are OPERATOR-GATED (§39.3.1) and reject   *
 * an anonymous console session with 403 — BY DESIGN, not a      *
 * failure and not "offline". Callers branch on MeshApiError.    *
 * ============================================================ */

/** One §42.4 pseudonymised consent-record projection. */
export interface ConsentRecordSummary {
  consent_id: string;
  state: string;
  country: string;
  citizen_pseudonym: string;
  consumer: string;
  holder: string;
  data_class: string;
  purpose: string;
  lawful_basis: string;
  consent_channel: string;
  valid_from: string | null;
  valid_until: string | null;
  single_use: boolean;
  issued_at: string;
  self_hash: string;
}

/** GET /v1/consent/records */
export interface ConsentRecordsResponse {
  country: string;
  chain_head: { seq: number | null; self_hash: string | null };
  records: ConsentRecordSummary[];
  page: { next_before_seq: number | null };
}

/** GET /v1/consent/records/{id} — a single record plus its Merkle proof handle. */
export interface ConsentRecordDetail extends ConsentRecordSummary {
  merkle_proof: { seq: number; leaf_self_hash: string };
}

/** Consent plane — consent records, USSD/SMS/SD-JWT-VC, RTBF. */
export const consent = {
  /** GET /v1/planes/consent/healthz — deployed-stub health probe. */
  health(opts?: RequestOpts): Promise<PlaneHealth> {
    return probePlaneHealth("consent", opts);
  },
  /** GET /v1/consent/records — consent records under the operator gate.
   * Anonymous callers get 403 (MeshApiError) — access-gated, not offline. */
  listRecords(): Promise<ConsentRecordsResponse> {
    return planeFetchJson("consent", "/v1/consent/records", "consent-service");
  },
  /** GET /v1/consent/records/{id} — a single consent record + proof handle.
   * Anonymous callers get 403 (MeshApiError) — access-gated, not offline. */
  getRecord(id: string): Promise<ConsentRecordDetail> {
    return planeFetchJson(
      "consent",
      `/v1/consent/records/${encodeURIComponent(id)}`,
      "consent-service",
    );
  },
  /** POST /v1/consent/rtbf — right-to-be-forgotten erasure request.
   * No such route is served yet (the real §47.3 erasure lives at
   * POST /v1/me/erasure-request with different semantics) — stays offline. */
  requestErasure(_subject: string): Promise<never> {
    return Promise.reject(offline("Consent", "consent-rtbf"));
  },
};

/* ============================================================ *
 * Audit ledger (Observability plane) — Part VI.                 *
 * Wire shapes mirror services/audit_plane/read_api.py.          *
 * ============================================================ */

/** One §39.3.1 non-identifying chain-integrity row. */
export interface AuditEntry {
  seq: number;
  event_kind: string;
  created_at: string;
  self_hash_hex: string;
  prev_hash_hex: string;
}

/** GET /v1/audit/entries */
export interface AuditEntriesResponse {
  shard: string;
  head_seq: number | null;
  tree_size: number;
  entries: AuditEntry[];
  page: { next_before_seq: number | null };
}

/** GET /v1/audit/verify — advisory chain-integrity walk. */
export interface AuditVerifyResult {
  from_seq: number;
  to_seq: number;
  shard: string;
  chain_ok: boolean;
  signatures_ok: boolean;
  /** True when the plane has NO verification keys wired (deployment gap) —
   * chain_ok:false with this set means "cannot verify", NOT "chain broken".
   * The UI must never render the two the same way. */
  verification_key_unresolved: boolean;
  first_divergence: unknown;
  advisory: string;
}

/** Audit ledger (Observability plane) — hash-chained audit, forgetting service. */
export const audit = {
  /** GET /v1/planes/audit/healthz — deployed-stub health probe. */
  health(opts?: RequestOpts): Promise<PlaneHealth> {
    return probePlaneHealth("audit", opts);
  },
  /** GET /v1/audit/entries — hash-chained audit ledger entries. */
  listEntries(): Promise<AuditEntriesResponse> {
    return planeFetchJson("audit", "/v1/audit/entries", "audit-ledger");
  },
  /** GET /v1/audit/entries/{hash} — one entry by its self-hash.
   * No such route is served yet — stays a genuine offline stub. */
  getEntry(_hash: string): Promise<never> {
    return Promise.reject(offline("Observability", "audit-ledger"));
  },
  /** GET /v1/audit/verify — verify the chain head integrity.
   * 404 (empty ledger) / 400 (empty window) surface as MeshApiError. */
  verifyChain(): Promise<AuditVerifyResult> {
    return planeFetchJson("audit", "/v1/audit/verify", "audit-ledger");
  },
  /** POST /v1/audit/forget — HMAC-erasable-key forgetting (right to be forgotten).
   * No such route is served yet — stays a genuine offline stub. */
  forget(_key: string): Promise<never> {
    return Promise.reject(offline("Observability", "forgetting-service"));
  },
};
